import { useState } from "react";
import { useSelector } from "react-redux";
import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getNegotiationInsightLog } from "../../api/caseNegotiations";
import useErrorHandler from "../../hooks/useErrorHandler";
import { fmtDateTime } from "./negotiation-ui";

// Admin-only: every AI assistant answer given for this negotiation. Loaded the first time it is expanded.
export default function AiAdviceLog({ caseId, negId }) {
  const { data: user } = useSelector((state) => state.user);
  const errorHandler = useErrorHandler();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  async function onChange(_, expanded) {
    if (!expanded || rows || loading) return;
    setLoading(true);
    try {
      const { data } = await getNegotiationInsightLog(user.token, caseId, negId);
      setRows(data.data || []);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Accordion variant="outlined" disableGutters onChange={onChange} className="mt-4 rounded-xl before:hidden">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>AI advice log</Typography>
        <Typography fontSize="small" color="text.secondary" className="!ml-3 self-center">
          Admin only
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {loading ? (
          <Box className="py-4 grid place-items-center">
            <CircularProgress size={24} />
          </Box>
        ) : rows && !rows.length ? (
          <Typography color="text.secondary">No AI advice has been requested for this negotiation.</Typography>
        ) : (
          (rows || []).map((r) => (
            <Box key={r.id} className="border-0 border-t border-solid border-[#eef1f5] py-2 first:border-t-0">
              <Box className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <Typography fontWeight={600}>{r.user_name || "Unknown user"}</Typography>
                <Typography fontSize="small" color="text.secondary">
                  {fmtDateTime(r.created_at)}
                </Typography>
                <Typography fontSize="small" color="text.secondary">
                  Source: {r.source}
                </Typography>
                {r.perspective ? (
                  <Typography fontSize="small" color="text.secondary">
                    View: {r.perspective}
                  </Typography>
                ) : null}
                {r.likelihood !== null && r.likelihood !== undefined ? (
                  <Typography fontSize="small" color="text.secondary">
                    Estimated settle chance: {r.likelihood}%
                  </Typography>
                ) : null}
              </Box>
              <Typography className="mt-1">{r.summary}</Typography>
              {r.suggested_next_step ? (
                <Typography fontSize="small" color="text.secondary">
                  Next step: {r.suggested_next_step}
                </Typography>
              ) : null}
            </Box>
          ))
        )}
      </AccordionDetails>
    </Accordion>
  );
}
